import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Paper, Button, TextField, Link as MuiLink, CircularProgress, Alert } from '@mui/material';
import LoginForm from '../components/auth/LoginForm';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import usePageTitle from '../hooks/usePageTitle';

type WelcomeCopy = {
  title: string;
  subtitle: string;
};

const defaultWelcomeCopy: WelcomeCopy = {
  title: 'Welcome Back',
  subtitle: 'Sign in to access your simulations and ideas'
};

const directSourceCopy: Record<string, WelcomeCopy> = {
  landing: {
    title: 'Welcome to WaveScoop!',
    subtitle: 'Create an account to start forecasting reactions to your content'
  },
  pricing: {
    title: 'Get Started with WaveScoop',
    subtitle: 'Sign up to begin your free trial - no credit card required'
  },
  blog: {
    title: 'Get Started with WaveScoop',
    subtitle: 'Sign up to begin your free trial - no credit card required'
  },
  'try-now': {
    title: 'Ready to Try WaveScoop?',
    subtitle: 'Sign in or create an account to generate ideas and run simulations in minutes'
  }
};

const ideaSourceCopy: WelcomeCopy = {
  title: 'Bring Your Ideas to Life Faster',
  subtitle: 'Sign in to turn your brief into audience-tuned content ideas'
};

const simulationSourceCopy: WelcomeCopy = {
  title: 'Run a Simulation Before You Publish',
  subtitle: 'Sign in to predict reactions and de-risk your next campaign'
};

const getWelcomeCopy = (rawSource: string | null): WelcomeCopy => {
  if (!rawSource) {
    return defaultWelcomeCopy;
  }

  const normalized = rawSource.toLowerCase();

  if (directSourceCopy[normalized]) {
    return directSourceCopy[normalized];
  }

  if (normalized.includes('idea')) {
    return ideaSourceCopy;
  }

  if (normalized.includes('simulation')) {
    return simulationSourceCopy;
  }

  return defaultWelcomeCopy;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, isAuthenticated } = useAuth();
  const [welcomeText, setWelcomeText] = useState<WelcomeCopy>(() => getWelcomeCopy(new URLSearchParams(location.search).get('source')));
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  
  // Get source query param for redirection after login
  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get('source');

  usePageTitle('Login - WaveScoop', false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      logger.info('User is already authenticated, redirecting to dashboard...');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setWelcomeText(getWelcomeCopy(source));
  }, [source]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // ... existing code ...
  };

  // Don't render the form if already authenticated (while redirecting)
  if (isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Redirecting to dashboard...</Typography>
      </Box>
    );
  }

  return (
    <PublicPageLayout>
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm" sx={{ pt: 8, pb: 6 }}>
          <Paper 
            component="section"
            elevation={3} 
            sx={{ 
              p: 4, 
              bgcolor: 'rgba(15, 51, 89, 0.65)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
                {welcomeText.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {welcomeText.subtitle}
              </Typography>
            </Box>
            <LoginForm />
          </Paper>
        </Container>
      </Box>
    </PublicPageLayout>
  );
};

export default LoginPage; 
